import { Link } from 'react-router';
import { SUITE_LABELS, type SuiteId } from '@commerce-os/shared-types';
// Subpath import — the pos-core barrel pulls react-native into Vite.
import {
  planLockedSuite,
  useFeatureFlagStore,
} from '@commerce-os/pos-core/features/featureFlagsStore';

/**
 * Phase 64 — why a page or section isn't showing, told honestly.
 *
 * Two different causes, two different fixes:
 *   - the org's PLAN doesn't include the feature's suite → the fix is the
 *     plan, so link billing (an owner can't switch on what the plan lacks);
 *   - an owner turned the feature OFF → the fix is the features page.
 * The flag store carries the entitled suites from `GET /features`, which is
 * what tells the two apart.
 */
export function PlanLockedNotice({ suite }: { suite: SuiteId }) {
  return (
    <div className="flex items-center justify-center py-20 px-6">
      <div className="max-w-md text-center space-y-3">
        <h2 className="text-xl font-semibold">Not included in your plan</h2>
        <p className="text-default-500 text-sm">
          {SUITE_LABELS[suite]} isn't part of your current subscription. Upgrade your
          plan to unlock it.
        </p>
        <div className="flex items-center justify-center gap-3 pt-2">
          <Link
            to="/settings/billing"
            className="rounded-md border border-default-300 px-4 py-2 text-sm font-medium hover:bg-default-100"
          >
            Compare plans
          </Link>
          <Link to="/" className="text-sm font-medium text-default-500 hover:text-default-700">
            Back home
          </Link>
        </div>
      </div>
    </div>
  );
}

export function FeatureUnavailableNotice({ featureId }: { featureId: string }) {
  const lockedSuite = useFeatureFlagStore((s) => planLockedSuite(featureId, s.entitledSuites));
  if (lockedSuite) return <PlanLockedNotice suite={lockedSuite} />;
  return (
    <div className="flex items-center justify-center py-20 px-6">
      <div className="max-w-md text-center space-y-3">
        <h2 className="text-xl font-semibold">This feature isn't enabled</h2>
        <p className="text-default-500 text-sm">
          The <span className="font-mono text-default-700">{featureId}</span> feature is
          turned off for your organization. An owner can turn it on in Settings →
          Features.
        </p>
        <div className="flex items-center justify-center gap-3 pt-2">
          <Link
            to="/settings/features"
            className="rounded-md border border-default-300 px-4 py-2 text-sm font-medium hover:bg-default-100"
          >
            Open Features
          </Link>
          <Link to="/" className="text-sm font-medium text-default-500 hover:text-default-700">
            Back home
          </Link>
        </div>
      </div>
    </div>
  );
}
